/**
 * @file ServicesPage.jsx
 * @description Portafolio de servicios de AGDYSA con integración de carrito de cotizaciones.
 * @author JEMAprod-Tecnologia (2026)
 */

import { useState, useEffect, useMemo, cloneElement, isValidElement } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck,
  Activity,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  X,
  Maximize2,
  Shield,
  Cpu,
  ShoppingCart,
  FileText,
  Stethoscope,
  Volume2,
  Thermometer,
  Snowflake,
  Radiation,
} from "lucide-react";
import { BrandIcon } from "../components/BrandIcon";
import { GreenIconTile, TILE_ICON_SIZE } from "../components/GreenIconTile";
import data from "../data/servicesData.json";

// INTEGRACIÓN DEL CONTEXTO DE COTIZACIÓN
import { useQuotation } from "../context/QuotationContext";
import TrainingLayout from "../modules/TrainingLayout";
import trainingData from "../data/trainingServices.json";
import { useCmsDoc } from "../hooks/useCmsDoc";

/**
 * Mapeo de iconos para servicios y sub-servicios.
 * Se incluyen definiciones adicionales para garantizar funcionalidad.
 */
const iconMap = {
  eye: <BrandIcon id="visiometria" size={TILE_ICON_SIZE} />,
  heartPulse: <BrandIcon id="electrocardiograma" size={TILE_ICON_SIZE} />,
  scan: <BrandIcon id="rayos-x" size={TILE_ICON_SIZE} />,
  wind: <BrandIcon id="espirometria" size={TILE_ICON_SIZE} />,
  headphones: <BrandIcon id="audiometria" size={TILE_ICON_SIZE} />,
  waves: <BrandIcon id="ultrasonografia" size={TILE_ICON_SIZE} />,
  microscope: <BrandIcon id="laboratorio-clinico" size={TILE_ICON_SIZE} />,
  zap: <BrandIcon id="mediciones-higienicas" size={TILE_ICON_SIZE} />,
  group: <BrandIcon id="grupo-trabajadores" size={TILE_ICON_SIZE} />,
  medical: <BrandIcon id="preocupacionales" size={52} />,
  labor: <BrandIcon id="ocupacionales" size={52} />,
  post: <BrandIcon id="postocupacionales" size={52} />,
  evalua: <BrandIcon id="evaluacion-medica" size={52} />,
  muestra: <BrandIcon id="toma-muestras" size={52} />,
  resultado: <BrandIcon id="analisis-interpretacion" size={52} />,
  examen: <BrandIcon id="examenes-complementarios" size={52} />,
  capacitacion: <BrandIcon id="capacitacion-prevencion" size={52} />,
  sun: <BrandIcon id="proteccion-solar" size={TILE_ICON_SIZE} />,
  flask: <BrandIcon id="laboratorio-clinico" size={TILE_ICON_SIZE} />,
  volume: <Volume2 size={TILE_ICON_SIZE} />,
  thermometer: <Thermometer size={TILE_ICON_SIZE} />,
  snowflake: <Snowflake size={TILE_ICON_SIZE} />,
  activity: <Activity size={TILE_ICON_SIZE} />,
  radiation: <Radiation size={TILE_ICON_SIZE} />,
};

function renderTileIcon(iconKey) {
  const icon = iconMap[iconKey];
  if (!icon) return <Activity size={TILE_ICON_SIZE} />;
  if (!isValidElement(icon)) return icon;
  const size = icon.props.size ?? TILE_ICON_SIZE;
  return cloneElement(icon, { size, className: undefined });
}

const ServicesPage = () => {
  const catalogFallback = useMemo(
    () => ({
      servicesPage: data.servicesPage,
      servicesData: data.servicesData,
      training: trainingData,
    }),
    []
  );
  const { data: catalog } = useCmsDoc("services", "catalog", catalogFallback);
  const servicesPage = catalog?.servicesPage || data.servicesPage;
  const allServices = catalog?.servicesData || data.servicesData;
  const training = catalog?.training || trainingData;

  const [activeId, setActiveId] = useState(data.servicesData[0].id);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);
  
  // HOOKS DE CARRITO
  const { addItem, showToast } = useQuotation();

  useEffect(() => {
    if (allServices?.length && !allServices.some((s) => s.id === activeId)) {
      setActiveId(allServices[0].id);
    }
  }, [allServices, activeId]);

  /**
   * Bloqueo de scroll para el visualizador HD
   */
  useEffect(() => {
    if (selectedInstrument) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [selectedInstrument]);

  /**
   * Agrega un servicio o instrumento al carrito y redirige a cotización
   * @param {Object} item - Objeto del servicio o instrumento
   * @param {string} category - Categoría del servicio para el reporte
   */
  const handleAddToQuotation = (item, category) => {
    try {
      const result = addItem({
        id: item.id || Math.random().toString(36).substr(2, 9),
        name: item.name || item.title,
        category: category,
      });
      if (result.duplicate) {
        showToast("Este servicio ya está en tu cotización");
      } else if (result.ok) {
        showToast("Añadido a tu cotización");
      }
    } catch (error) {
      console.error("[AUDITORIA] Error al agregar item a cotización:", error);
    }
  };

  const toggleService = (id) => setActiveId(activeId === id ? null : id);
  
  const activeService =
    allServices.find((s) => s.id === activeId) || allServices[0];

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.pageX - left) / width) * 100;
    const y = ((e.pageY - top) / height) * 100;
    setZoomPos({ x, y });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#fafcfb] min-h-screen font-body pb-20"
    >
      {/* 1. HERO EDITORIAL */}
      <header className="relative pt-40 pb-20 bg-brand-dark overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#39ff14 1px, transparent 1px), linear-gradient(90deg, #39ff14 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="w-8 h-[2px] bg-brand-accent" />
              <span className="text-brand-accent font-bold uppercase tracking-[0.4em] text-[10px] md:text-sm">
                Soluciones de Ingeniería Humana
              </span>
            </motion.div>

            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="font-display text-[clamp(2.5rem,10vw,5.5rem)] text-white font-black leading-[1.1] md:leading-[0.9] tracking-tighter"
            >
              {servicesPage.title.split(" ")[0]} <br className="md:hidden" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent to-emerald-400 italic font-semibold">
                & {servicesPage.title.split(" ")[2]}
              </span>
            </motion.h1>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-white/80 text-lg md:text-2xl font-light mt-5 max-w-2xl border-l-2 border-brand-accent/30 pl-6 leading-relaxed"
            >
              {servicesPage.subtitle}
            </motion.p>
          </div>
        </div>
      </header>

      {/* 2. DASHBOARD DE SERVICIOS */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          {/* MÓVIL: ACORDEÓN */}
          <div className="lg:hidden space-y-4">
            {allServices.map((svc) => (
              <div key={svc.id}>
                <button
                  onClick={() => toggleService(svc.id)}
                  className={`w-full text-left p-6 rounded-[24px] flex items-center justify-between border transition-all duration-500 ${
                    activeId === svc.id
                      ? "bg-brand-dark text-white shadow-2xl"
                      : "bg-white text-brand-dark border-brand-soft"
                  }`}
                >
                  <div>
                    <p className={`text-[8px] font-bold uppercase tracking-widest mb-1 ${activeId === svc.id ? "text-brand-accent" : "text-brand-primary"}`}>
                      {svc.sidebarTitle}
                    </p>
                    <h3 className="font-display font-extrabold text-lg leading-tight tracking-tight">
                      {svc.title}
                    </h3>
                  </div>
                  <motion.div animate={{ rotate: activeId === svc.id ? 180 : 0 }}>
                    <ChevronDown size={20} className={activeId === svc.id ? "text-brand-accent" : "opacity-30"} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {activeId === svc.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-white border-x border-b border-brand-soft rounded-b-[24px] -mt-2 p-6 overflow-hidden"
                    >
                      
                      {activeId === "capacitaciones-asesorias" ? (
                        <TrainingLayout 
                          data={training} 
                          onAddToQuotation={handleAddToQuotation} 
                        />
                      ) : (
                      <ServiceContent
                        service={svc}
                        onSelectInstrument={setSelectedInstrument}
                        onAddToQuotation={handleAddToQuotation}
                      />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* DESKTOP: SIDEBAR + CONTENT */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 space-y-4 sticky top-32">
              {allServices.map((svc) => (
                <button
                  key={svc.id}
                  onClick={() => setActiveId(svc.id)}
                  className={`w-full text-left p-8 rounded-[32px] transition-all duration-500 flex items-center justify-between group ${
                    activeId === svc.id
                      ? "bg-brand-dark text-white shadow-xl scale-[1.05]"
                      : "bg-white text-brand-dark border border-brand-soft hover:shadow-lg"
                  }`}
                >
                  <div className="max-w-[85%]">
                    <p className={`text-[9px] font-bold uppercase tracking-[0.2em] mb-2 ${activeId === svc.id ? "text-brand-accent" : "text-brand-primary"}`}>
                      {svc.sidebarTitle}
                    </p>
                    <h3 className="font-display font-extrabold text-base tracking-tight leading-tight">
                      {svc.title}
                    </h3>
                  </div>
                  <ChevronRight className={activeId === svc.id ? "text-brand-accent translate-x-1" : "opacity-10"} />
                </button>
              ))}
            </div>

            <div className="lg:col-span-8">
              <motion.div
                key={activeId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[60px] p-16 shadow-2xl border border-brand-soft min-h-[700px]"
              >
                {activeId === "capacitaciones-asesorias" ? (
                  <TrainingLayout
                    data={training}
                    onAddToQuotation={handleAddToQuotation}
                  />
                ) : (
                  <ServiceContent
                    service={activeService}
                    onSelectInstrument={setSelectedInstrument}
                    onAddToQuotation={handleAddToQuotation}
                  />
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. MODAL DE INSTRUMENTOS HD */}
      <AnimatePresence>
        {selectedInstrument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-start md:items-center justify-center p-0 md:p-12 bg-brand-dark/95 backdrop-blur-xl overflow-y-auto"
            onClick={() => setSelectedInstrument(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-6xl w-full bg-white rounded-none md:rounded-[48px] overflow-hidden shadow-2xl flex flex-col md:flex-row my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-6 right-6 z-50 w-12 h-12 bg-brand-dark text-white rounded-full flex items-center justify-center shadow-2xl hover:bg-brand-accent transition-colors"
                onClick={() => setSelectedInstrument(null)}
              >
                <X size={32} />
              </button>

              <div
                className="w-full md:w-3/5 bg-[#f8faf9] p-4 md:p-12 flex items-center justify-center min-h-[40vh] md:min-h-[600px] overflow-hidden cursor-zoom-in relative"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <motion.img
                  layoutId={`img-${selectedInstrument.id}`}
                  src={selectedInstrument.img}
                  className="max-h-[300px] md:max-h-[500px] object-contain drop-shadow-2xl"
                  style={{ transformOrigin: `${zoomPos.x}% ${zoomPos.y}%` }}
                  animate={{ scale: isHovered ? 1.6 : 1 }}
                  transition={{ type: "spring", stiffness: 150, damping: 25 }}
                />
                {!isHovered && (
                  <div className="absolute bottom-6 flex items-center gap-2 text-brand-dark/30 animate-pulse">
                    <Maximize2 size={12} />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Zoom Interactivo</span>
                  </div>
                )}
              </div>

              <div className="w-full md:w-2/5 p-12 bg-white flex flex-col justify-between border-t md:border-t-0 md:border-l border-brand-soft">
                <div className="space-y-8">
                  <header>
                    <span className="bg-brand-primary/10 text-brand-primary px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest mb-3 inline-block">
                      Instrumental Acreditado
                    </span>
                    <h3 className="font-display text-4xl font-extrabold text-brand-dark leading-tight tracking-tighter">
                      {selectedInstrument.name}
                    </h3>
                  </header>
                  <div className="space-y-6">
                    <div className="flex gap-4 group">
                      <GreenIconTile rounded="rounded-xl">
                        <Cpu size={TILE_ICON_SIZE} />
                      </GreenIconTile>
                      <div>
                        <p className="text-[9px] font-bold uppercase text-brand-dark/40 tracking-widest mb-1">Modelo de Alta Precisión</p>
                        <p className="text-lg font-bold text-brand-dark italic">{selectedInstrument.equip}</p>
                      </div>
                    </div>
                    <div className="flex gap-4 group">
                      <GreenIconTile rounded="rounded-xl">
                        <Shield size={TILE_ICON_SIZE} />
                      </GreenIconTile>
                      <div>
                        <p className="text-[9px] font-bold uppercase text-brand-dark/40 tracking-widest mb-1">Validación Normativa</p>
                        <p className="text-sm font-medium text-brand-dark/70">Certificado bajo protocolos internacionales para auditorías de cumplimiento.</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* BOTÓN CARRITO MODAL */}
                <button 
                  onClick={() => handleAddToQuotation(selectedInstrument, "Higiene Industrial")}
                  className="mt-12 w-full bg-brand-accent text-brand-dark py-5 rounded-[20px] font-black uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-white hover:text-brand-primary border-2 border-transparent hover:border-brand-accent transition-all shadow-xl active:scale-95"
                >
                  <ShoppingCart size={20} /> Incorporar a Cotización
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// --- COMPONENTE DE CONTENIDO DETALLADO ---
const ServiceContent = ({ service, onSelectInstrument, onAddToQuotation }) => {
  return (
    <div className="space-y-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <h2 className="font-display text-4xl md:text-5xl font-black text-brand-dark leading-tight tracking-tighter">
          {service.title}
        </h2>
        {/* BOTÓN CARRITO SERVICIO PRINCIPAL */}
        {service.id !== "higiene-industrial" && (
          <button 
            onClick={() => onAddToQuotation(service, service.sidebarTitle)}
            className="flex items-center gap-2 bg-brand-accent text-brand-dark px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest hover:brightness-110 transition-all shadow-lg shadow-brand-accent/20"
          >
            <ShoppingCart size={16} /> Cotizar Servicio
          </button>
        )}
      </div>

      <p className="font-body text-xl text-brand-dark/70 font-light italic border-l-4 border-brand-accent pl-8 leading-relaxed">
        {service.description || service.descriptionM1 || service.description1}
      </p>

      {/* Medicina del Trabajo / Tareas */}
      {service.tasks && (
        <div className="space-y-8">
          <p className="text-brand-primary font-bold uppercase tracking-widest text-lg text-center border-b border-brand-soft pb-4 flex items-center justify-center gap-3">
            <Stethoscope size={24} /> {service.tasksTitle}
          </p>
          <div className="grid gap-4">
            {service.tasks.map((t, i) => (
              <div key={i} className="flex gap-4 items-start group p-4 bg-gray-50 rounded-2xl border border-transparent hover:border-brand-soft transition-all">
                <div className="w-5 h-5 shrink-0 rounded-full border-4 border-brand-accent bg-white mt-1 group-hover:scale-125 transition-transform" />
                <p className="font-body text-lg text-brand-dark/80 font-normal leading-snug">{t}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sub-servicios con Iconos (Exámenes Médicos) */}
      {service.subServices && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 ">
          {service.subServices.map((sub, i) => (
            <div key={i} className="group flex h-full flex-col justify-between gap-6 p-6 md:p-8 bg-[#f8faf9] rounded-[32px] md:rounded-[40px] border border-transparent hover:border-brand-accent/20 hover:bg-white hover:shadow-2xl transition-all duration-500">
              <div className="space-y-6">
                <GreenIconTile rounded="rounded-[28px]">
                  {renderTileIcon(sub.icon)}
                </GreenIconTile>
                <div className="space-y-4">
                  <h4 className="font-display text-xl md:text-1xl font-black text-brand-dark tracking-tight leading-snug">
                    {sub.title}
                  </h4>
                  <p className="font-body text-sm md:text-base text-brand-dark/80 leading-relaxed">
                    {sub.desc}
                  </p>
                </div>
              </div>

              

                {sub.note && (
                  <div className="inline-flex items-center gap-2 bg-brand-primary/10 px-4 py-3 rounded-2xl text-xs md:text-sm font-bold text-brand-primary shadow-sm border border-brand-primary/20">
                    <ShieldCheck size={16} className="text-brand-accent" />
                    <span>{sub.note}</span>
                  </div>
                )}
              </div>
          ))}
        </div>
      )}

      {/* Programa Monitoreo Biomarcador (Steps) */}
      {/* --- SECCIÓN DE CONTENIDO REFACTORIZADA PARA SERVICEPAGE.JSX --- */}
        <div className="space-y-12">
          {/* Cabecera de Servicio y Botón de Cotización Directa 
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <h2 className="font-display text-4xl md:text-5xl font-black text-brand-dark leading-tight tracking-tighter">
              {service.title}
            </h2>
            <button 
              onClick={() => onAddToQuotation(service, service.sidebarTitle || "Servicio Técnico")}
              className="flex items-center gap-2 bg-brand-accent text-brand-dark px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-white hover:shadow-lg border-2 border-transparent hover:border-brand-accent transition-all active:scale-95"
            >
              <ShoppingCart size={16} /> Cotizar este Servicio
            </button>
          </div>*/}

          {/* Bloque de Descripciones Principales */}
          <div className="space-y-6">
                      {/* Evaluaciones Médicas para Tareas Críticas */}
          {service.evaluations && service.evaluations.length > 0 && (
            <div className="space-y-8">
              <div className="px-6 py-6 lg:px-10 lg:py-8 bg-[#f7fbf9] rounded-[40px] border border-brand-soft shadow-sm">
                <div className="max-w-3xl mx-auto">
                  <p className="text-brand-accent font-bold uppercase tracking-[0.35em] text-[10px] md:text-[11px] mb-3">{service.evaluationsTitle}</p>
                  <h3 className="font-display  text-justify text-2xl md:text-3xl font-black text-brand-dark leading-tight mb-4">
                    {service.evaluationsSubtitle}
                  </h3>
                  <p className="font-body text-base md:text-lg text-justify text-brand-dark/75 leading-relaxed md:px-12">
                    {service.evaluationsDescription}
                  </p>
                  <p className="font-body text-base md:text-lg  text-justify text-brand-dark/75 leading-relaxed md:px-12 mt-4">
                    {service.evaluationsFooter}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {service.evaluations.map((evaluation) => (
                  <div key={evaluation.id} className="bg-white rounded-[40px] border border-brand-soft shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                    <div className="p-8 md:p-10">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div>
                          <p className="text-brand-accent font-semibold uppercase tracking-[0.35em] text-[10px] md:text-[11px]">
                            Evaluación Crítica
                          </p>
                          <h4 className="font-display text-2xl md:text-3xl font-black text-brand-dark leading-tight mt-2">
                            {evaluation.title}
                          </h4>
                        </div>
                        <button
                          onClick={() => onAddToQuotation({ id: evaluation.id, title: evaluation.title }, "Evaluaciones Médicas Críticas")}
                          className="inline-flex items-center justify-center rounded-full bg-brand-accent text-brand-dark px-4 py-3 text-xs md:text-sm font-bold uppercase tracking-[0.2em] shadow-md hover:bg-white hover:text-brand-primary border-2 border-transparent hover:border-brand-accent transition-all"
                        >
                          <ShoppingCart size={16} className="mr-2" /> Cotizar evaluación
                        </button>
                      </div>

                      <p className="mt-6 text-base md:text-lg text-brand-dark/80 leading-relaxed">
                        {evaluation.summary}
                      </p>

                      <div className="mt-6 space-y-3">
                        {evaluation.items.map((item, idx) => (
                          <div key={idx} className="flex gap-3">
                            <span className="mt-1 inline-flex h-2.5 w-2.5 rounded-full bg-brand-accent" />
                            <p className="font-body text-sm md:text-base text-brand-dark/80 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

            {service.description2 && (
              <p className="font-body text-xl text-brand-dark font-light pl-2 text-justify leading-relaxed">
                {service.description2}
              </p>
            )}
            {service.description3 && (
              <p className="font-body text-xl text-brand-dark font-bold italic bg-brand-soft/20 border-l-4 border-brand-accent p-6 rounded-r-3xl text-justify leading-relaxed">
                {service.description3}
              </p>
            )}
            {service.description4 && (
              <p className="font-body text-xl text-brand-dark font-light pl-2 text-justify leading-relaxed">
                {service.description4}
              </p>
            )}
          </div>



          {/* Sección Legal (Específica para Monitoreo de Biomarcadores) */}
          {service.id === "monitoreo-biomarcador" && (
            <div className="space-y-6">
              <div id="programa-legal" className="px-10 py-6 bg-brand-dark rounded-[50px] relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent opacity-10 blur-3xl" />
                <p className="text-brand-accent font-body font-bold uppercase tracking-[0.4em] text-[10px] mb-2 relative z-10">Marco Regulatorio</p>
                <h3 className="text-white font-display text-3xl font-black tracking-tighter relative z-10 leading-tight">
                  {service.legaltitle || service.legal}
                </h3>
              </div>
              
              <div className="grid gap-4 bg-surface p-8 rounded-[40px] border border-brand-soft shadow-inner">
                {service.legalacu1 && <p className="font-body text-lg text-brand-dark font-light leading-relaxed italic">"{service.legalacu1}"</p>}
                {service.legalacu2 && <p className="font-body text-lg text-brand-dark font-light leading-relaxed italic">"{service.legalacu2}"</p>}
              </div>
            </div>
          )}

          {/* Sección de Pasos y Metodología (StepsM) */}
          {service.stepsM && (
            <div className="space-y-8">
              <div className="px-10 py-4 bg-brand-dark rounded-[50px] relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent opacity-10 blur-3xl" />
                <h3 className="text-brand-accent font-display text-2xl font-black text-center tracking-tighter relative z-10">
                  Metodología del Programa
                </h3>
              </div>
              
              <div className="grid gap-4">
                {service.stepsM.map((t, i) => (
                  <div key={i} className="flex gap-6 items-start p-4 hover:bg-white hover:shadow-md rounded-2xl transition-all group">
                    <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-dark text-brand-accent flex items-center justify-center font-bold">{i + 1}</span>
                    <p className="font-body text-lg text-brand-dark/80 font-medium leading-snug pt-1">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grid de Exámenes Médicos / Sub-servicios (subServicesM) */}
          {service.subServicesM && (
            <div className="space-y-8">
              <div className="px-10 py-4 bg-brand-dark rounded-[50px] relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent opacity-10 blur-3xl" />
                <h3 className="text-brand-accent font-display text-2xl font-black text-center tracking-tighter relative z-10">
                  Componentes de Evaluación Clínica
                </h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {service.subServicesM.map((sub, i) => (
                  <div key={i} className="p-8 bg-white rounded-[40px] border border-brand-soft shadow-sm hover:shadow-xl hover:border-brand-accent/20 transition-all duration-500 group">
                    <GreenIconTile className="mb-6" rounded="rounded-2xl">
                      {renderTileIcon(sub.icon)}
                    </GreenIconTile>
                    <h4 className="font-display text-2xl font-black text-brand-dark mb-3 tracking-tight">{sub.title}</h4>
                    <p className="font-body text-brand-dark/70 text-lg font-light leading-relaxed mb-4">{sub.desc}</p>
                    
                    {((sub.stepsex || sub.steps) ?? []).length > 0 && (
                      <div className="space-y-2.5 pt-4 border-t border-brand-soft">
                        {(sub.stepsex || sub.steps).map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-brand-accent mt-2 shrink-0" />
                            <span className="text-sm font-semibold text-brand-dark/80 leading-snug break-words">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sección de Beneficios y Cierre de Reglas */}
          {(service.steps2 || service.rule1) && (
            <div className="space-y-8 bg-brand-dark p-10 md:p-14 rounded-[60px] text-white shadow-2xl relative overflow-hidden">
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-brand-accent opacity-5 blur-3xl" />
              
              {service.descriptionM4 && <h3 className="font-display text-3xl font-bold text-brand-accent mb-8">{service.descriptionM4}</h3>}
              
              {service.steps2 && (
                <div className="grid gap-4">
                  {service.steps2.map((t, i) => (
                    <div key={i} className="flex gap-4 items-center group">
                      <CheckCircle className="text-brand-accent shrink-0" size={20} />
                      <p className="font-body text-xl text-white font-bold leading-snug">{t}</p>
                    </div>
                  ))}
                </div>
              )}

              {service.rule1 && (
                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="text-brand-accent font-bold uppercase tracking-widest text-[10px] mb-4">{service.rule || "Condiciones de Servicio"}</p>
                  <p className="font-body text-2xl text-white font-light italic leading-relaxed">
                    {service.rule1}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

      {/* Higiene Industrial (Instrumental) */}
      {service.instrumental && (
        <div className="space-y-12">
          <div className="p-10 bg-brand-dark rounded-[50px] relative overflow-hidden shadow-2xl text-center">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent opacity-10 blur-3xl" />
            <h3 className="text-brand-accent font-display text-3xl font-black italic tracking-tighter relative z-10 leading-tight">
              "{service.slogan}"
            </h3>
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.4em] mt-4 relative z-10">Unidad de Ingeniería Higiénica</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {service.instrumental.map((inst) => (
              <div key={inst.id} className="group bg-white rounded-[40px] border border-brand-soft overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500">
                <div className="p-6 bg-brand-dark flex items-center justify-between">
                  <span className="font-display font-bold text-[10px] text-white uppercase tracking-[0.2em]">{inst.name}</span>
                  <div className="flex gap-2">
                    {/* Botón Carrito Individual */}
                    <button 
                      onClick={() => onAddToQuotation(inst, "Higiene Industrial")}
                      className="w-10 h-10 rounded-xl bg-white/10 text-brand-accent flex items-center justify-center hover:bg-brand-accent hover:text-brand-dark transition-all shadow-sm"
                      title="Agregar a mi cotización"
                    >
                      <ShoppingCart size={18} />
                    </button>
                    <button 
                      onClick={() => onSelectInstrument(inst)} 
                      className="w-10 h-10 rounded-xl bg-brand-accent flex items-center justify-center text-brand-dark hover:scale-110 transition-transform"
                    >
                      <Maximize2 size={18} strokeWidth={3} />
                    </button>
                  </div>
                </div>
                <div className="aspect-[4/3] p-10 cursor-pointer overflow-hidden flex items-center justify-center" onClick={() => onSelectInstrument(inst)}>
                  <img src={inst.img} className="max-h-full w-auto object-contain transition-all duration-700 ease-in-out group-hover:scale-110" alt={inst.name} />
                </div>
                <div className="p-5 text-center bg-[#f8faf9] border-t border-brand-soft">
                  <p className="font-body text-[10px] text-brand-dark font-black uppercase tracking-widest">{inst.equip}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer de Contenido */}
      <div className="pt-10 border-t border-brand-soft flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <FileText size={20} className="text-brand-primary opacity-40" />
          <p className="text-[11px] font-bold uppercase tracking-widest text-brand-dark/40">Consultoría Técnica AGDYSA-LABSYSO</p>
        </div>
        <button 
          onClick={() => onAddToQuotation(service, service.sidebarTitle)}
          className="text-brand-primary font-bold text-sm underline underline-offset-8 hover:text-brand-accent transition-colors"
        >
          Agregar todo este bloque a mi cotización
        </button>
      </div>
    </div>
  );
};

export default ServicesPage;